/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import {
	Group,
	Modal,
	ModalProps,
	Stack,
} from '@mantine/core';
import { useForm } from '@mantine/form';

// styles
import classes from './ModalSendCertificate.module.css';

// lodash
import { REGEX_EMAIL } from '@config/constants/regex';
import { TSelectOption } from '@global-types/index';
import concat from 'lodash/concat';
import isNil from 'lodash/isNil';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { LanguageSelect } from '../LanguageSelect/LanguageSelect';
import { Select } from '../Select/Select';
import { TextInput } from '../TextInput';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */
const LANGUAGES_OPTIONS = [
	{
		label: 'Français',
		value: 'fr',
		flagUrl: 'https://flagcdn.com/w80/fr.png',
	},
	{
		label: 'English',
		value: 'en',
		flagUrl: 'https://flagcdn.com/w80/gb.png',
	},
];

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type TModalSendCertificateProps = Partial<ModalProps> & {
	recepientOptions: TSelectOption[];
	handlesubmit: (values: {
		other?: string;
		recepient: string | null;
		language: string;
	}) => void;
	handleclose: () => void;
	loading?: boolean;
	opened: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ModalSendCertificate = (
	props: TModalSendCertificateProps
) => {
	/* ******************************** Hooks ******************************* */
	const { t } = useTranslation();

	const form = useForm({
		initialValues: {
			recepient: null,
			other: '',
			language: 'fr',
		},

		validate: {
			recepient: (value) =>
				isNil(value)
					? t('global.recepientRequired')
					: null,
			other: (value, values) =>
				!REGEX_EMAIL.test(value) &&
				values.recepient == 'other'
					? t('form.error.invalidEmail')
					: null,
			language: (value) =>
				isNil(value)
					? t('global.languageRequired')
					: null,
		},
	});

	const recipientOptions = concat(
		[],
		props.recepientOptions,
		{
			label: t('global.other'),
			value: 'other',
		}
	);
	const handleClose = () => {
		props.handleclose();
		form.reset();
	};

	useEffect(() => {
		if (!props.opened) {
			handleClose();
		}
	}, [props.opened]);

	return (
		<Modal
			title={t('global.sendMailForm')}
			classNames={{ title: classes.title }}
			opened={props.opened}
			onClose={handleClose}
		>
			<form onSubmit={form.onSubmit(props.handlesubmit)}>
				<Stack gap={'xs'}>
					<Select
						withAsterisk
						data={recipientOptions}
						placeholder={t('global.recipientOptions')}
						{...form.getInputProps('recepient')}
					/>
					{form.getValues().recepient === 'other' && (
						<TextInput
							withAsterisk
							{...form.getInputProps('other')}
							placeholder={t(
								'placeholders.enterRecipientEmail'
							)}
						/>
					)}

					<LanguageSelect
						value={form.values.language}
						onChange={(val) =>
							form.setFieldValue('language', val)
						}
						options={LANGUAGES_OPTIONS}
						width={390}
						fullWidth={false}
					/>
				</Stack>
				<Group mt="lg" justify="flex-end">
					<Button
						variant={'primary'}
						type="submit"
						disabled={props.loading}
						loading={props.loading}
					>
						{t('global.send')}
					</Button>
					<Button
						variant={'transparent'}
						onClick={handleClose}
					>
						{t('global.cancel')}
					</Button>
				</Group>
			</form>
		</Modal>
	);
};
